import React, { useState } from "react";
import { Sparkles, Plus, X, Loader2, ShieldCheck, TrendingUp, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import api from "../lib/apiClient";

const QUADRANTS = [
  { key: "strengths", title: "Strengths", tag: "Internal", accent: "emerald", desc: "Things you control & do well" },
  { key: "weaknesses", title: "Weaknesses", tag: "Internal", accent: "amber", desc: "Internal gaps to improve" },
  { key: "opportunities", title: "Opportunities", tag: "External", accent: "sky", desc: "Market openings to seize" },
  { key: "threats", title: "Threats", tag: "External", accent: "rose", desc: "External risks to prepare for" },
];

const ACCENT = {
  emerald: { border: "border-emerald-500/40", bg: "bg-emerald-500/5", text: "text-emerald-400", badge: "bg-emerald-500/15 text-emerald-300" },
  amber: { border: "border-amber-500/40", bg: "bg-amber-500/5", text: "text-amber-400", badge: "bg-amber-500/15 text-amber-300" },
  sky: { border: "border-sky-500/40", bg: "bg-sky-500/5", text: "text-sky-400", badge: "bg-sky-500/15 text-sky-300" },
  rose: { border: "border-rose-500/40", bg: "bg-rose-500/5", text: "text-rose-400", badge: "bg-rose-500/15 text-rose-300" },
};

const PRIO = {
  high: "bg-rose-500/15 text-rose-300",
  medium: "bg-amber-500/15 text-amber-300",
  low: "bg-slate-600/40 text-slate-300",
};

function Quadrant({ q, plan, items, onChange, readOnly, premium, onUpgrade }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const a = ACCENT[q.accent];

  const add = (val, priority = "medium") => {
    const t = (val ?? text).trim();
    if (!t) return;
    onChange([...items, { id: `i_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, text: t, priority }]);
    setText("");
  };

  const remove = (id) => onChange(items.filter((i) => i.id !== id));

  const aiSuggest = async () => {
    setLoading(true);
    try {
      const res = await api.post("/ai/suggest", {
        type: q.key, business_name: plan.business_name, industry: plan.industry, description: plan.description,
      });
      const existing = new Set(items.map((i) => i.text.toLowerCase()));
      const fresh = res.data.items.filter((i) => !existing.has(i.text.toLowerCase()));
      onChange([...items, ...fresh]);
      toast.success(`Added ${fresh.length} AI suggestion${fresh.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI suggestion failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`swot-quadrant-${q.key}`} className={`rounded-2xl border ${a.border} ${a.bg} p-5 flex flex-col`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className={`font-display text-lg font-bold ${a.text}`}>{q.title}</h3>
            <Badge className={a.badge}>{q.tag === "Internal" ? <ShieldCheck className="h-3 w-3 mr-1" /> : <TrendingUp className="h-3 w-3 mr-1" />}{q.tag}</Badge>
          </div>
          <p className="text-xs text-slate-400 mt-1">{q.desc}</p>
        </div>
        {!readOnly && (
          <Button data-testid={`swot-ai-${q.key}`} size="sm" variant="ghost" onClick={aiSuggest} disabled={loading} className={`${a.text} hover:bg-white/5 shrink-0`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" /> AI</>}
          </Button>
        )}
      </div>

      <div className="space-y-2 mt-4 flex-1">
        {items.length === 0 && <p className="text-sm text-slate-500 italic">No items yet.</p>}
        {items.map((i) => {
          const locked = i.priority === "high" && !premium;
          return (
            <div key={i.id} data-testid={`swot-item-${i.id}`} className="group flex items-center gap-2 rounded-lg bg-slate-900/60 border border-slate-800 px-3 py-2">
              {locked ? (
                <button
                  data-testid={`swot-locked-${i.id}`}
                  onClick={onUpgrade}
                  title="Upgrade to view this high-impact insight"
                  className="flex-1 flex items-center gap-2 text-left"
                >
                  <span className="text-sm text-slate-200 flex-1 blur-[5px] select-none pointer-events-none">{i.text}</span>
                  <Lock className="h-3.5 w-3.5 text-orange-400 shrink-0" />
                </button>
              ) : (
                <span className="text-sm text-slate-200 flex-1">{i.text}</span>
              )}
              <Badge className={`${PRIO[i.priority] || PRIO.medium} text-[10px] uppercase`}>{i.priority}</Badge>
              {!readOnly && (
                <button data-testid={`swot-remove-${i.id}`} onClick={() => remove(i.id)} className="text-slate-500 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!readOnly && (
        <div className="flex gap-2 mt-4">
          <Input
            data-testid={`swot-input-${q.key}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder={`Add a ${q.title.toLowerCase().slice(0, -1)}…`}
            className="bg-slate-900/70 border-slate-700 text-sm"
          />
          <Button data-testid={`swot-add-${q.key}`} size="icon" onClick={() => add()} className="bg-slate-700 hover:bg-slate-600 shrink-0">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function SwotBoard({ plan, value, onChange, readOnly, premium = true, onUpgrade }) {
  const setQuad = (key, items) => onChange({ ...value, [key]: items });
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {QUADRANTS.map((q) => (
        <Quadrant key={q.key} q={q} plan={plan} items={value[q.key] || []} onChange={(items) => setQuad(q.key, items)} readOnly={readOnly} premium={premium} onUpgrade={onUpgrade} />
      ))}
    </div>
  );
}
